import classNames from 'classnames';
import { h, JSX } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';

import { Loader } from './Loader';

// Bounds for the auto-scale factor. Consumers that size a viewport box around the plan (e.g. the
// admin seat-map preview) must honour MIN_AUTO_SCALE so the box is tall enough not to clip the map.
export const MIN_AUTO_SCALE = 0.5;
export const MAX_AUTO_SCALE = 2.0;

// Debounce window for recomputing the scale when the viewport box resizes, so a drag fires one recalculation.
const RESIZE_DEBOUNCE_MS = 150;

interface FloorplanSymbol {
    class?: string;
    text: string;
}

type TextKey = 'screen' | 'entrance' | 'stage' | 'balcony' | 'sideBalcony' | 'lodges';

export interface FloorplanProps {
    class?: string;
    texts: Record<TextKey, string>;
    data: FloorplanData;
    onClickSeat?: (rowNumber: number, seatNumber: number, sectionId?: string) => unknown;
    isLoading?: boolean;
    showZoomControls?: boolean;
    autoScale?: boolean;
    // Height of the auto-scaled viewport. Defaults to filling the parent, which must then give it a
    // definite height (e.g. a flex area); a compact value (e.g. "300px") fits the plan into a small preview box.
    autoScaleHeight?: string;
    showLegend?: boolean;
}

export interface Seat {
    number: number;
    isBooked?: boolean;
    isSold?: boolean;
    isReserved?: boolean;
    isTaken?: boolean;
    isUnavailable?: boolean;
    isDisabled?: boolean;
    icon?: string;
}

interface SeatRow {
    number: number;
    seats: (Seat | null)[];
    numberOnLeft?: boolean;
    numberOnRight?: boolean;
    icon?: string;
}

interface SeatGrid {
    id: string;
    x: number;
    y: number;
    rotation?: number;
    sectionId?: string;
    rows: SeatRow[];
}

type LabelStyle = 'small' | 'large';

interface Label {
    x: number;
    y: number;
    rotation?: number;
    textKey: TextKey;
    style: LabelStyle;
}

interface Pos {
    x: number;
    y: number;
}

export interface FloorplanData {
    width: number;
    height: number;
    grids: SeatGrid[];
    sections?: {
        id: string;
        name: string;
        x?: number;
        y?: number;
    }[];
    labels?: Label[];
    screenTextPos?: Pos; // Backwards compatibility with older floorplans, can be removed soon
    entryTextPos?: Pos; // Backwards compatibility with older floorplans, can be removed soon
    symbols?: FloorplanSymbol[];
    minWidth?: number;
    maxWidth?: number;
    maxHeight?: number;
    showZoomControls?: boolean;
}

const SEAT_SIZE = 24;
const ROW_HEIGHT = 32;
// Pointer movement (px) past which a press is treated as a pan rather than a click.
const DRAG_THRESHOLD = 5;

const getSeatBackgroundPos = (iconName: string | undefined): { x: number; y: number } => {
    if (
        iconName === 'super' ||
        iconName === 'down' ||
        iconName === 'pair-left' ||
        iconName === 'pair-right' ||
        iconName === 'pari-left-down' ||
        iconName === 'pari-right-down' ||
        iconName === 'wheelchair-down' ||
        iconName === 'wheelchair-companion-down'
    ) {
        return { x: -3, y: -3 };
    }

    return { x: -3, y: 1.5 };
};

const getSeatIconPath = (iconName: string | undefined): string => {
    if (iconName === 'super') {
        return 'M1 0h15s1 0 1 1v3s0 1-1 1H1S0 5 0 4V1s0-1 1-1M1 5.536h2s1 0 1 1V17s0 1-1 1H1s-1 0-1-1V6.536s0-1 1-1M14 5.536h2s1 0 1 1V17s0 1-1 1h-2s-1 0-1-1V6.536s0-1 1-1M4.414 5.467h8.104v11.52H4.414z';
    } else if (iconName === 'pair-left') {
        return 'M20 5H4.5c-2 0-3.8 1.5-4 3.6l-.5 5C-.2 15.9 1.6 18 4 18h16zm0-5H3.1c-.8 0-1.5.7-1.5 1.5S2.3 3 3.1 3H20Z';
    } else if (iconName === 'pair-right') {
        return 'M-2 5h15.5c2 0 3.8 1.5 4 3.6l.5 5c.2 2.3-1.6 4.4-4 4.4H-2Zm0-5h16.9c.8 0 1.5.7 1.5 1.5S15.7 3 14.9 3H-2Z';
    } else if (iconName === 'down') {
        return 'M.6 8.6c.2-2 1.9-3.6 4-3.6h8c2 0 3.8 1.5 4 3.6l.5 5c.2 2.3-1.6 4.4-4 4.4H4c-2.4 0-4.2-2.1-4-4.4l.6-5ZM3 0h11c.8 0 1.5.7 1.5 1.5S14.8 3 14 3H3c-.8 0-1.5-.7-1.5-1.5S2.2 0 3 0Z';
    } else if (iconName === 'wheelchair' || iconName === 'wheelchair-companion') {
        return 'M0.361015 8.83218C0.166676 6.50011 2.00704 4.5 4.3472 4.5H13.6528C15.993 4.5 17.8333 6.5001 17.639 8.83218L16.8057 18.8322C16.6329 20.9053 14.8998 22.5 12.8195 22.5H5.18053C3.10018 22.5 1.36711 20.9054 1.19435 18.8322L0.361015 8.83218ZZ';
    } else if (iconName === 'wheelchair-down' || iconName === 'wheelchair-companion-down') {
        return 'M17.639 13.6678C17.8333 15.9999 15.993 18 13.6528 18L4.3472 18C2.00704 18 0.166676 15.9999 0.361016 13.6678L1.19435 3.66782C1.36711 1.59465 3.10018 -1.30258e-06 5.18053 -1.12071e-06L12.8195 -4.52896e-07C14.8998 -2.71026e-07 16.6329 1.59465 16.8057 3.66782L17.639 13.6678Z';
    } else if (iconName === 'pari-left-down') {
        return 'M20 19H4.5c-2 0-3.8-1.5-4-3.6l-.5-5C-.2 8.1 1.6 6 4 6h16zm0 5H3.1c-.8 0-1.5-.7-1.5-1.5S2.3 21 3.1 21H20Z';
    } else if (iconName === 'pari-right-down') {
        return 'M-2 19h15.5c2 0 3.8-1.5 4-3.6l.5-5c.2-2.3-1.6-4.4-4-4.4H-2Zm0 5h16.9c.8 0 1.5-.7 1.5-1.5S15.7 21 14.9 21H-2Z';
    } else {
        return 'M16.5 13.9c-.2 2-1.9 3.6-4 3.6h-8c-2 0-3.8-1.5-4-3.6l-.5-5c-.2-2.3 1.6-4.4 4-4.4h9.1c2.4 0 4.2 2.1 4 4.4l-.6 5zm-2.4 8.6h-11c-.8 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5h11c.8 0 1.5.7 1.5 1.5s-.7 1.5-1.5 1.5z';
    }
};

// Glyph icons (wheelchair, companion) draw the figure in a single contrast colour
// that depends on the seat's state, replacing the usual seat number.
const getGlyphFillColor = (seat: Seat): string => {
    if (seat.isBooked) {
        return '#525252';
    }
    if (seat.isDisabled) {
        return '#a3a3a3';
    }
    return '#ffffff';
};

const getWheelchairIconPaths = (seat: Seat, iconName: 'wheelchair' | 'wheelchair-down'): JSX.Element => {
    const fillColor = getGlyphFillColor(seat);

    return (
        <g transform={iconName === 'wheelchair-down' ? 'translate(0 -4.5)' : ''}>
            <path
                d="M10.7162 16.0016C10.3939 17.2541 9.25704 18.1795 7.90385 18.1795C6.3002 18.1795 5 16.8793 5 15.2756C5 14.1037 5.69426 13.09388 6.69391 12.63508M7.90385 10.43589L8.38783 14.7917L12.0176 14.5497L12.7436 17.2115H13.4696M7.90385 10.43589C8.16057 10.43589 8.40678 10.33392 8.5883 10.15239C8.76983 9.97087 8.87181 9.72466 8.87181 9.46795C8.87181 9.21123 8.76983 8.96503 8.5883 8.78351C8.40678 8.60198 8.16058 8.5 7.90386 8.5C7.64714 8.5 7.40094 8.60198 7.21942 8.78351C7.03789 8.96503 6.93591 9.21123 6.93591 9.46795C6.93591 9.72466 7.03789 9.97087 7.21942 10.15239C7.40094 10.33392 7.64714 10.43589 7.90385 10.43589ZM9.59777 12.3718H11.5337"
                stroke={fillColor}
                stroke-width="0.93"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
            <path
                d="M8.87181 9.46795C8.87181 9.72466 8.76983 9.97087 8.5883 10.15239C8.40678 10.33392 8.16057 10.43589 7.90385 10.43589C7.64714 10.43589 7.40094 10.33392 7.21942 10.15239C7.03789 9.97087 6.93591 9.72466 6.93591 9.46795C6.93591 9.21123 7.03789 8.96503 7.21942 8.78351C7.40094 8.60198 7.64714 8.5 7.90386 8.5C8.16058 8.5 8.40678 8.60198 8.5883 8.78351C8.76983 8.96503 8.87181 9.21123 8.87181 9.46795ZM9.59777 12.3718H11.5337H9.59777Z"
                fill={fillColor}
            />
        </g>
    );
};

// Companion seat: the wheelchair figure (identical to the wheelchair icon, nudged
// right to make room) with a person pushing it on the left. Marks seats kept for
// someone accompanying a wheelchair user.
const getCompanionIconPaths = (
    seat: Seat,
    iconName: 'wheelchair-companion' | 'wheelchair-companion-down'
): JSX.Element => {
    const fillColor = getGlyphFillColor(seat);

    // Scale the whole composition down a little (around the seat centre) so the two
    // figures sit inset with margin instead of touching the seat edges.
    return (
        <g
            transform={
                iconName === 'wheelchair-companion-down'
                    ? 'translate(0 -4.5) translate(9 13) scale(0.8) translate(-9 -13)'
                    : 'translate(9 13) scale(0.8) translate(-9 -13)'
            }
        >
            <g transform="translate(2 0)">
                <path
                    d="M10.7162 16.0016C10.3939 17.2541 9.25704 18.1795 7.90385 18.1795C6.3002 18.1795 5 16.8793 5 15.2756C5 14.1037 5.69426 13.09388 6.69391 12.63508M7.90385 10.43589L8.38783 14.7917L12.0176 14.5497L12.7436 17.2115H13.4696M7.90385 10.43589C8.16057 10.43589 8.40678 10.33392 8.5883 10.15239C8.76983 9.97087 8.87181 9.72466 8.87181 9.46795C8.87181 9.21123 8.76983 8.96503 8.5883 8.78351C8.40678 8.60198 8.16058 8.5 7.90386 8.5C7.64714 8.5 7.40094 8.60198 7.21942 8.78351C7.03789 8.96503 6.93591 9.21123 6.93591 9.46795C6.93591 9.72466 7.03789 9.97087 7.21942 10.15239C7.40094 10.33392 7.64714 10.43589 7.90385 10.43589ZM9.59777 12.3718H11.5337"
                    stroke={fillColor}
                    stroke-width="0.93"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M8.87181 9.46795C8.87181 9.72466 8.76983 9.97087 8.5883 10.15239C8.40678 10.33392 8.16057 10.43589 7.90385 10.43589C7.64714 10.43589 7.40094 10.33392 7.21942 10.15239C7.03789 9.97087 6.93591 9.72466 6.93591 9.46795C6.93591 9.21123 7.03789 8.96503 7.21942 8.78351C7.40094 8.60198 7.64714 8.5 7.90386 8.5C8.16058 8.5 8.40678 8.60198 8.5883 8.78351C8.76983 8.96503 8.87181 9.21123 8.87181 9.46795ZM9.59777 12.3718H11.5337H9.59777Z"
                    fill={fillColor}
                />
            </g>
            <circle cx="3.5" cy="8.8" r="0.97" stroke={fillColor} stroke-width="0.93" fill={fillColor} />
            <path
                d="M3.5 10.0 L3.5 10.85"
                stroke={fillColor}
                stroke-width="0.93"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
            <path
                d="M3.5 10.85 L3.6 14.1"
                stroke={fillColor}
                stroke-width="0.93"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
            <path
                d="M3.55 11.2 L7.8 12.05"
                stroke={fillColor}
                stroke-width="0.93"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
            <path
                d="M3.6 14.1 L2.7 17.8"
                stroke={fillColor}
                stroke-width="0.93"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
            <path
                d="M3.6 14.1 L4.6 17.6"
                stroke={fillColor}
                stroke-width="0.93"
                stroke-linecap="round"
                stroke-linejoin="round"
            />
        </g>
    );
};

interface SeatProps {
    floorplanProps: FloorplanProps;
    row: SeatRow;
    seat: Seat;
    seatIndex: number;
    sectionId?: string;
}

const SeatComponent = (props: SeatProps): JSX.Element => {
    const { floorplanProps, row, seat, seatIndex, sectionId } = props;
    const iconName = seat.icon ?? row.icon;

    const isWheelChair = iconName === 'wheelchair' || iconName === 'wheelchair-down';
    const isCompanion = iconName === 'wheelchair-companion' || iconName === 'wheelchair-companion-down';

    return (
        <g
            key={seat.number}
            class={classNames('floorplan__seat', {
                'is-booked': seat.isBooked,
                'is-reserved': seat.isReserved,
                'is-sold': seat.isSold,
                'is-taken': seat.isTaken,
                'is-unavailable': seat.isUnavailable,
                'is-disabled': seat.isDisabled,
            })}
            style={{ cursor: 'pointer' }}
            transform={`translate(${seatIndex * SEAT_SIZE} 0)`}
            onClick={(e: JSX.TargetedMouseEvent<SVGGElement>): unknown => {
                // Prevent double-firing when programmatically triggered
                if (!e.isTrusted) {
                    return floorplanProps.onClickSeat?.(row.number, seat.number, sectionId);
                }
                // Natural clicks are handled by mouseUp logic
                return undefined;
            }}
        >
            <rect class="floorplan__seat-background" {...getSeatBackgroundPos(iconName)} width="24" height="24" />
            <path d={getSeatIconPath(iconName)} />
            {isWheelChair && getWheelchairIconPaths(seat, iconName)}
            {isCompanion && getCompanionIconPaths(seat, iconName)}
            {!isWheelChair && !isCompanion && (
                <text className="floorplan__seat-number" x="8.5" y="12" text-anchor="middle" dominant-baseline="middle">
                    {seat.number}
                </text>
            )}
        </g>
    );
};

const renderRow = (props: FloorplanProps, row: SeatRow, rowIndex: number, sectionId?: string): JSX.Element => {
    let first = -1;
    let last = -1;

    for (const [index, seat] of row.seats.entries()) {
        if (seat) {
            last = index;

            if (first === -1) {
                first = index;
            }
        }
    }

    return (
        <g key={row.number} transform={`translate(0 ${rowIndex * ROW_HEIGHT})`}>
            {row.numberOnLeft && (
                <text
                    class="floorplan__row-number fill-neutral-400 text-[9px]"
                    x={first * SEAT_SIZE - 6}
                    y={SEAT_SIZE / 2}
                    text-anchor="end"
                    dominant-baseline="middle"
                >
                    {row.number}
                </text>
            )}
            {row.seats.map(
                (seat, seatIndex) =>
                    seat && (
                        <SeatComponent
                            floorplanProps={props}
                            row={row}
                            seat={seat}
                            seatIndex={seatIndex}
                            sectionId={sectionId}
                        />
                    )
            )}
            {row.numberOnRight && (
                <text
                    class="floorplan__row-number fill-neutral-400 text-[9px]"
                    x={last * SEAT_SIZE + 23}
                    y={SEAT_SIZE / 2}
                    dominant-baseline="middle"
                >
                    {row.number}
                </text>
            )}
        </g>
    );
};

const renderGrid = (props: FloorplanProps, grid: SeatGrid): JSX.Element => (
    <g key={grid.id} transform={`translate(${grid.x} ${grid.y}) rotate(${grid.rotation ?? 0})`}>
        {grid.rows.map((row, rowIndex) => renderRow(props, row, rowIndex, grid.sectionId))}
    </g>
);

export const Floorplan = (props: FloorplanProps): JSX.Element => {
    // Zoom and pan state
    const [zoomLevel, setZoomLevel] = useState(1);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [hasDraggedEnough, setHasDraggedEnough] = useState(false);
    const [clickStartTarget, setClickStartTarget] = useState<Element | null>(null);

    // Touch gesture state
    const [touchStartDistance, setTouchStartDistance] = useState<number | null>(null);
    const [touchStartScale, setTouchStartScale] = useState<number>(1);
    const [lastTap, setLastTap] = useState<number>(0);

    // Auto-scale state
    const [autoScaleFactor, setAutoScaleFactor] = useState(1);

    // Ref for the SVG container
    const svgContainerRef = useRef<HTMLDivElement>(null);

    // Detect Safari browser
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    // Zoom handlers
    const handleZoomIn = (): void => {
        if (!svgContainerRef.current) {
            setZoomLevel((prev) => Math.min(prev + 0.25, 5));
            return;
        }

        const rect = svgContainerRef.current.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const prevZoom = zoomLevel;
        const newZoom = Math.min(prevZoom + 0.25, 5);

        const svgCenterX = (centerX - panOffset.x) / prevZoom;
        const svgCenterY = (centerY - panOffset.y) / prevZoom;

        const newPanX = centerX - svgCenterX * newZoom;
        const newPanY = centerY - svgCenterY * newZoom;

        setZoomLevel(newZoom);
        setPanOffset({ x: newPanX, y: newPanY });
    };

    const handleZoomOut = (): void => {
        if (!svgContainerRef.current) {
            setZoomLevel((prev) => Math.max(prev - 0.25, 1));
            return;
        }

        const rect = svgContainerRef.current.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const prevZoom = zoomLevel;
        const newZoom = Math.max(prevZoom - 0.25, 1);

        const svgCenterX = (centerX - panOffset.x) / prevZoom;
        const svgCenterY = (centerY - panOffset.y) / prevZoom;

        const newPanX = centerX - svgCenterX * newZoom;
        const newPanY = centerY - svgCenterY * newZoom;

        setZoomLevel(newZoom);
        setPanOffset({ x: newPanX, y: newPanY });
    };

    const handleReset = (): void => {
        setZoomLevel(1);
        setPanOffset({ x: 0, y: 0 });
    };

    // Mouse event handlers for pan
    const handleMouseDown = (e: JSX.TargetedMouseEvent<HTMLDivElement>): void => {
        const target = e.target as Element;
        setClickStartTarget(target);

        setDragStart({
            x: e.clientX - panOffset.x,
            y: e.clientY - panOffset.y,
        });
        setIsPanning(true);
        setHasDraggedEnough(false);
    };

    const handleMouseMove = (e: JSX.TargetedMouseEvent<HTMLDivElement>): void => {
        if (!isPanning) {
            return;
        }

        const currentX = e.clientX - dragStart.x;
        const currentY = e.clientY - dragStart.y;
        const distance = Math.sqrt(Math.pow(currentX - panOffset.x, 2) + Math.pow(currentY - panOffset.y, 2));

        if (distance > DRAG_THRESHOLD) {
            setHasDraggedEnough(true);
            setPanOffset({ x: currentX, y: currentY });
        }
    };

    const handleMouseUp = (): void => {
        if (hasDraggedEnough) {
            setIsPanning(false);
            setClickStartTarget(null);
            return;
        }

        if (clickStartTarget) {
            const seatElement = clickStartTarget.closest('.floorplan__seat');
            if (seatElement) {
                const clickEvent = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                });
                seatElement.dispatchEvent(clickEvent);
            }
        }

        setIsPanning(false);
        setClickStartTarget(null);
    };

    const handleMouseLeave = (): void => {
        setIsPanning(false);
        setClickStartTarget(null);
    };

    // Helper functions for touch
    const getTouchDistance = (touches: TouchList): number => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    const getTouchCenter = (touches: TouchList): { x: number; y: number } => ({
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
    });

    // Touch event handlers
    const handleTouchStart = (e: TouchEvent): void => {
        if (e.touches.length === 1) {
            const touch = e.touches[0];
            setDragStart({
                x: touch.clientX - panOffset.x,
                y: touch.clientY - panOffset.y,
            });
            setIsPanning(true);
            setHasDraggedEnough(false);

            const target = e.target as Element;
            setClickStartTarget(target);

            const now = Date.now();
            if (now - lastTap < 300) {
                e.preventDefault();
                if (!svgContainerRef.current) {
                    return;
                }

                const rect = svgContainerRef.current.getBoundingClientRect();
                const tapX = touch.clientX - rect.left;
                const tapY = touch.clientY - rect.top;

                const prevZoom = zoomLevel;
                const newZoom = Math.min(prevZoom + 1, 5);

                const svgX = (tapX - panOffset.x) / prevZoom;
                const svgY = (tapY - panOffset.y) / prevZoom;

                setZoomLevel(newZoom);
                setPanOffset({
                    x: tapX - svgX * newZoom,
                    y: tapY - svgY * newZoom,
                });

                setLastTap(0);
            } else {
                setLastTap(now);
            }
        } else if (e.touches.length === 2) {
            e.preventDefault();
            setIsPanning(false);
            setTouchStartDistance(getTouchDistance(e.touches));
            setTouchStartScale(zoomLevel);
        }
    };

    const handleTouchMove = (e: TouchEvent): void => {
        if (e.touches.length === 1 && isPanning) {
            const touch = e.touches[0];
            const currentX = touch.clientX - dragStart.x;
            const currentY = touch.clientY - dragStart.y;
            const distance = Math.sqrt(Math.pow(currentX - panOffset.x, 2) + Math.pow(currentY - panOffset.y, 2));

            if (distance > DRAG_THRESHOLD) {
                setHasDraggedEnough(true);
                setPanOffset({ x: currentX, y: currentY });
            }
        } else if (e.touches.length === 2 && touchStartDistance !== null) {
            e.preventDefault();
            if (!svgContainerRef.current) {
                return;
            }

            const currentDistance = getTouchDistance(e.touches);
            const scale = currentDistance / touchStartDistance;
            const newZoom = Math.min(Math.max(touchStartScale * scale, 1), 5);

            const center = getTouchCenter(e.touches);
            const rect = svgContainerRef.current.getBoundingClientRect();
            const centerX = center.x - rect.left;
            const centerY = center.y - rect.top;

            const svgX = (centerX - panOffset.x) / zoomLevel;
            const svgY = (centerY - panOffset.y) / zoomLevel;

            setZoomLevel(newZoom);
            setPanOffset({
                x: centerX - svgX * newZoom,
                y: centerY - svgY * newZoom,
            });
        }
    };

    const handleTouchEnd = (e: TouchEvent): void => {
        if (e.touches.length === 0) {
            if (!hasDraggedEnough && clickStartTarget) {
                const seatElement = clickStartTarget.closest('.floorplan__seat');
                if (seatElement) {
                    const clickEvent = new MouseEvent('click', {
                        bubbles: true,
                        cancelable: true,
                        view: window,
                    });
                    seatElement.dispatchEvent(clickEvent);
                }
            }

            setIsPanning(false);
            setClickStartTarget(null);
            setTouchStartDistance(null);
        } else if (e.touches.length === 1) {
            const touch = e.touches[0];
            setDragStart({
                x: touch.clientX - panOffset.x,
                y: touch.clientY - panOffset.y,
            });
            setIsPanning(true);
            setHasDraggedEnough(false);
            setTouchStartDistance(null);
        }
    };

    // Mouse wheel zoom
    const handleWheel = (e: WheelEvent): void => {
        e.preventDefault();

        if (!svgContainerRef.current) {
            return;
        }

        const rect = svgContainerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const zoomSpeed = 0.1;
        const delta = e.deltaY > 0 ? -zoomSpeed : zoomSpeed;

        const prevZoom = zoomLevel;
        const newZoom = Math.min(Math.max(prevZoom + delta, 1), 5);

        const svgX = (mouseX - panOffset.x) / prevZoom;
        const svgY = (mouseY - panOffset.y) / prevZoom;

        const newPanX = mouseX - svgX * newZoom;
        const newPanY = mouseY - svgY * newZoom;

        setZoomLevel(newZoom);
        setPanOffset({ x: newPanX, y: newPanY });
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent): void => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }

            if (e.key === '+' || e.key === '=') {
                e.preventDefault();
                handleZoomIn();
            } else if (e.key === '-' || e.key === '_') {
                e.preventDefault();
                handleZoomOut();
            } else if (e.key === '0') {
                e.preventDefault();
                handleReset();
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [zoomLevel, panOffset]);

    // Auto-scale calculation
    useEffect(() => {
        if (!props.autoScale) {
            setAutoScaleFactor(1);
            return;
        }

        const calculateScale = (): void => {
            if (!svgContainerRef.current) {
                return;
            }

            // Get the actual container dimensions
            const containerRect = svgContainerRef.current.getBoundingClientRect();

            // Calculate available space with safety padding to prevent edge clipping
            const PADDING = 10; // 10px safety padding for polite margins
            const availableWidth = containerRect.width - PADDING;
            const availableHeight = containerRect.height - PADDING;

            const floorplanWidth = props.data.width;
            const floorplanHeight = props.data.height;

            // Calculate scale factors for both dimensions
            const widthScale = availableWidth / floorplanWidth;
            const heightScale = availableHeight / floorplanHeight;

            // Use the smaller scale to ensure entire floorplan fits
            const calculated = Math.min(widthScale, heightScale);

            const constrained = Math.min(Math.max(calculated, MIN_AUTO_SCALE), MAX_AUTO_SCALE);

            setAutoScaleFactor(constrained);
        };

        // Recalculate whenever the viewport box changes size: a window resize, or the content around it
        // growing (e.g. the customer screen's ticket table) and leaving the plan less room. The first
        // measurement is immediate; later ones are debounced so a drag triggers one recalculation at the end.
        let resizeTimer: number | undefined;
        let hasMeasured = false;
        const observer = new ResizeObserver(() => {
            if (!hasMeasured) {
                hasMeasured = true;
                calculateScale();
                return;
            }

            window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(calculateScale, RESIZE_DEBOUNCE_MS);
        });

        if (svgContainerRef.current) {
            observer.observe(svgContainerRef.current);
        }

        return () => {
            observer.disconnect();
            window.clearTimeout(resizeTimer);
        };
    }, [props.autoScale, props.data.width, props.data.height]);

    const className = classNames('floorplan relative', props.class, {
        'floorplan--loading opacity-50 delay-300': props.isLoading,
    });

    return (
        <div class={className}>
            <Loader isHidden={!props.isLoading} />
            <div class="floorplan__plan flex justify-center">
                <div
                    ref={svgContainerRef}
                    class={classNames('floorplan__plan-inner w-full text-center', {
                        'overflow-auto': !props.autoScale,
                        'overflow-hidden': props.autoScale,
                    })}
                    style={{
                        height: props.autoScale ? (props.autoScaleHeight ?? '100%') : 'auto',
                        maxHeight: props.autoScale ? 'none' : props.data.maxHeight || '800px',
                        touchAction: 'none',
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                    onWheel={handleWheel}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <svg
                        viewBox={`0 0 ${props.data.width} ${props.data.height}`}
                        class="mx-auto"
                        style={{
                            ...(props.autoScale
                                ? {
                                      width: `${props.data.width * autoScaleFactor * zoomLevel}px`,
                                      height: `${props.data.height * autoScaleFactor * zoomLevel}px`,
                                  }
                                : {
                                      minWidth: props.data.minWidth,
                                      maxWidth: props.data.maxWidth,
                                      maxHeight: props.data.maxHeight,
                                      transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                                      transformOrigin: '0 0',
                                  }),
                            transition: isPanning || isSafari ? 'none' : 'transform 0.15s ease-out',
                            cursor: zoomLevel > 1 ? 'move' : 'default',
                            userSelect: 'none',
                            shapeRendering: 'crispEdges',
                        }}
                    >
                        {props.data.grids.map((grid) => renderGrid(props, grid))}
                        {props.data.labels?.map((label, index) => (
                            <text
                                key={index}
                                class={classNames('floorplan__text fill-neutral-500', {
                                    'floorplan__text--small text-[9px]': label.style === 'small',
                                    'floorplan__text--large text-xs font-semibold opacity-70': label.style === 'large',
                                })}
                                transform={`translate(${label.x} ${label.y}) rotate(${label.rotation ?? 0})`}
                                text-anchor="middle"
                                dominant-baseline="middle"
                            >
                                {props.texts[label.textKey]}
                            </text>
                        ))}

                        {/* Backwards compatibility with older floorplans, can be removed soon */}
                        {props.data.screenTextPos && (
                            <text
                                class="floorplan__text floorplan__text--large fill-neutral-500 text-xs font-semibold opacity-70"
                                x={props.data.screenTextPos.x}
                                y={props.data.screenTextPos.y}
                                text-anchor="middle"
                                dominant-baseline="middle"
                            >
                                {props.texts.screen}
                            </text>
                        )}
                        {props.data.entryTextPos && (
                            <text
                                class="floorplan__text floorplan__text--small fill-neutral-500 text-[9px]"
                                x={props.data.entryTextPos.x}
                                y={props.data.entryTextPos.y}
                                text-anchor="middle"
                                dominant-baseline="middle"
                            >
                                {props.texts.entrance}
                            </text>
                        )}
                    </svg>
                </div>
            </div>
            {/* Seat status legend - shown on the customer-facing monitor */}
            {props.showLegend && props.data.symbols && (
                <div class="floorplan__symbols">
                    {props.data.symbols.map((symbol, index) => (
                        <div
                            key={index}
                            class={classNames('floorplan__symbol', {
                                'is-booked': symbol.class === 'is-booked',
                                'is-taken': symbol.class === 'is-taken',
                                'is-disabled': symbol.class === 'is-disabled',
                            })}
                        >
                            <svg class="icon floorplan__symbol-icon" viewBox="0 0 18 24">
                                <path d={getSeatIconPath(undefined)} />
                            </svg>
                            <span class="floorplan__symbol-text">{symbol.text}</span>
                        </div>
                    ))}
                </div>
            )}
            {/* Zoom Controls - always rendered, visibility controlled via inline style */}
            <div
                class="floorplan__zoom-controls"
                style={{ display: props.showZoomControls === false ? 'none' : 'flex' }}
            >
                <button
                    class="floorplan__zoom-btn"
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 1}
                    aria-label="Zoom out"
                >
                    −
                </button>
                <button
                    class="floorplan__zoom-btn"
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 5}
                    aria-label="Zoom in"
                >
                    +
                </button>
                <button class="floorplan__zoom-btn" onClick={handleReset} aria-label="Reset zoom">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M3.86 6.18C4.96 3.69 7.29 2 10 2C13.87 2 17 5.13 17 9C17 9.12 17 9.24 16.99 9.36M14.14 11.82C13.04 14.31 10.71 16 8 16C4.13 16 1 12.87 1 9C1 8.88 1 8.76 1.01 8.64"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M1 3L1 6L4 6"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M17 15L17 12L14 12"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
};
